import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    LoadingSpinner,
    QueryErrorAlert,
    TextLink,
} from '@shared/components';
import { HolderNameChangeForm } from '@/features/customers/components/HolderDetails/HolderNameChangeForm';
import { useAgreementQuery } from '../hooks/useAgreementQuery';
import { useAgreementHolder } from '../hooks/useAgreementHolder';

const headingId = 'agreement-customer-name-edit-heading';

export function AgreementCustomerNameEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const agreementQuery = useAgreementQuery(id);
    const holder = useAgreementHolder(agreementQuery.data);

    const backToCustomer = () => navigate(`/agreements/${id}`, { state: { tab: 'customer' } });

    if (!id) {
        return <QueryErrorAlert message="Invalid agreement reference." />;
    }

    if (agreementQuery.isLoading) {
        return <LoadingSpinner message="Loading agreement" className="p-4" />;
    }

    if (agreementQuery.isError || !agreementQuery.data) {
        return <QueryErrorAlert message="Agreement not found or failed to load." />;
    }

    if (holder.source === 'pending') {
        return <LoadingSpinner message="Loading the customer" className="p-4" />;
    }

    // The form seeds from the live record and the write is permissioned, so a
    // caller who cannot make the change is sent back rather than shown a form.
    if (!holder.canChangeName || !holder.liveCustomer?.id) {
        return (
            <Alert variant="error">
                <div className="flex flex-col items-start gap-2">
                    <p>The holder's name cannot be changed from this plan.</p>
                    <Button type="button" size="sm" variant="outline" onClick={backToCustomer}>
                        Back to the customer
                    </Button>
                </div>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            <TextLink to={`/agreements/${id}`} state={{ tab: 'customer' }} color="secondary" className="text-sm">
                &larr; Back to {agreementQuery.data.agreementRef}
            </TextLink>

            <Card>
                <CardHeader>
                    <CardTitle>
                        <span id={headingId}>Change name</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <section aria-labelledby={headingId}>
                        <HolderNameChangeForm
                            customer={holder.liveCustomer}
                            agreementId={id}
                            onCancel={backToCustomer}
                            onSaved={backToCustomer}
                        />
                    </section>
                </CardContent>
            </Card>
        </div>
    );
}
